"use client";

import { useEffect, useMemo, useState } from "react";
import { iNoteWithGalleryBlockData } from "@/@sdk/holiday-quotes-x/holiday-quotes-x-model";
import {
  quoteThemeColorStyles,
  quoteThemeCssVars,
} from "../quote-theme/quote-theme-css-vars";
import { QuoteOverlayModal } from "../quote-overlay-modal";
import { NoteGalleryPanel } from "./note-gallery-panel";
import {
  countWordsInHtml,
  truncateHtmlByWords,
} from "../helpers/truncate-html-by-words";
import { cn } from "@/lib/utils";

interface NoteWithGallerySectionProps {
  heading?: string;
  noteWithGalleryData?: iNoteWithGalleryBlockData;
}

const PREVIEW_WORDS = 55;

function plainTextFromHtml(html: string) {
  return html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}

export function NoteWithGallerySection({
  heading = "Notes",
  noteWithGalleryData,
}: NoteWithGallerySectionProps) {
  const body = noteWithGalleryData?.body ?? "";
  const gallery = noteWithGalleryData?.gallery ?? [];
  const items = noteWithGalleryData?.items ?? [];
  const hasBody = Boolean(body && plainTextFromHtml(body));
  const [readMoreOpen, setReadMoreOpen] = useState(false);
  const [preview, setPreview] = useState<{
    html: string;
    truncated: boolean;
  }>({ html: body, truncated: false });

  useEffect(() => {
    if (!hasBody) {
      setPreview({ html: "", truncated: false });
      return;
    }
    const wordCount = countWordsInHtml(body);
    if (wordCount <= PREVIEW_WORDS) {
      setPreview({ html: body, truncated: false });
      return;
    }
    setPreview(truncateHtmlByWords(body, PREVIEW_WORDS));
  }, [body, hasBody]);

  const bullets = useMemo(() => {
    if (items.length === 0) return null;
    return (
      <ul className="space-y-3">
        {items.map((item) => (
          <li key={item.id} className="flex items-start gap-3">
            <span
              className="mt-1.5 h-2 w-2 rotate-45 flex-shrink-0"
              style={quoteThemeColorStyles.primaryBg}
              aria-hidden
            />
            <span className="text-sm sm:text-base text-gray-700 leading-relaxed">
              {item.text}
            </span>
          </li>
        ))}
      </ul>
    );
  }, [items]);

  if (!hasBody && gallery.length === 0 && items.length === 0) {
    return (
      <section
        className="bg-white p-4 sm:p-6"
        style={{
          borderRadius: quoteThemeCssVars.radiusCard,
          boxShadow: quoteThemeCssVars.shadowCard,
        }}
      >
        <p className="text-sm text-gray-500 text-center">No notes added</p>
      </section>
    );
  }

  const proseClass =
    "prose max-w-none text-sm sm:text-base focus:outline-none [&>ul]:list-disc [&>ul]:ml-4 [&>ol]:list-decimal [&>ol]:ml-4 [&>ul]:my-1 [&>ol]:my-1 [&>ul>li]:my-0 [&>ol>li]:my-0 prose-li:my-0 prose-li:leading-normal [&_li]:my-0 [&_li_p]:m-0";

  const contentColumn = (
    <div className="min-w-0 space-y-4">
      {hasBody ? (
        <div
          className={proseClass}
          dangerouslySetInnerHTML={{ __html: preview.html }}
        />
      ) : null}
      {preview.truncated ? (
        <button
          type="button"
          onClick={() => setReadMoreOpen(true)}
          className="flex w-full items-center justify-center rounded-lg px-3 py-2.5 text-sm font-semibold text-white"
          style={quoteThemeColorStyles.primaryButton}
        >
          Read more
        </button>
      ) : null}
      {bullets}
    </div>
  );

  return (
    <>
      <section
        className="@container space-y-4 bg-white p-4 sm:space-y-5 sm:p-6"
        style={{
          borderRadius: quoteThemeCssVars.radiusCard,
          boxShadow: quoteThemeCssVars.shadowCard,
        }}
      >
        <h2
          className="text-xl font-semibold sm:text-2xl"
          style={{
            color: quoteThemeCssVars.blockHeading,
            fontFamily: quoteThemeCssVars.headingFont,
          }}
        >
          {heading}
        </h2>

        {gallery.length > 0 ? (
          <div
            className={cn(
              "grid grid-cols-1 gap-5 items-stretch",
              // Container query (not viewport): two columns once the notes card itself is wide enough.
              "@[900px]:grid-cols-2"
            )}
          >
            <div className="min-h-[220px] min-w-0 sm:min-h-[300px] @[900px]:min-h-[280px] @[900px]:sm:min-h-[360px]">
              <NoteGalleryPanel images={gallery} />
            </div>
            {contentColumn}
          </div>
        ) : (
          contentColumn
        )}
      </section>

      <QuoteOverlayModal
        open={readMoreOpen}
        onClose={() => setReadMoreOpen(false)}
        title={heading || "Notes"}
      >
        {gallery.length > 0 ? (
          <div className="mb-4">
            <NoteGalleryPanel
              images={gallery}
              minHeightClassName="min-h-[200px] sm:min-h-[260px]"
            />
          </div>
        ) : null}
        {hasBody ? (
          <div
            className={proseClass}
            dangerouslySetInnerHTML={{ __html: body }}
          />
        ) : null}
        {bullets ? <div className="mt-4">{bullets}</div> : null}
      </QuoteOverlayModal>
    </>
  );
}
